import React, { useState } from 'react'
import BEMHelper from '@ynap/bem-helper'
import { LineItem } from '@ynap/line-item'
import { Notification, Tooltip } from '@ynap/notification'
import { Button } from '@ynap/button'
import { Reservation, ReservationProps, DispatchAction } from '../types'
import { cartActions } from '@ynap/cart-state'
import { useContactUs } from '@ynap/contact-us-hook'

const bem = new BEMHelper('ReservationsItem')

type ItemProps = {
    reservation: Reservation
    reservationsInCart: Array<string>
    setReservationsInCart: React.Dispatch<React.SetStateAction<string[]>>
    addToCart: DispatchAction<typeof cartActions.addToCart>
    onClick: Function
} & Pick<ReservationProps, 'addToCart' | 'addToCartResponse' | 'messages'>

export const ReservationItem: React.FC<ItemProps> = ({
    reservation,
    addToCart,
    addToCartResponse,
    reservationsInCart,
    setReservationsInCart,
    onClick,
    messages
}) => {
    const [addToCartRequested, setAddToCartRequested] = useState(false)
    const { reservationId, details } = reservation
    const itemFailed = addToCartResponse?.error?.body?.partNumber
    const itemAdded = reservationsInCart.includes(reservation.reservationId)
    const canAddToBag = details?.selectedProductVariant?.buyable?.canAddToBag

    const errorMessage = messages?.notifications?.errors?.({
        error: addToCartResponse?.error?.body?.error?.errorKey,
        single: true
    })

    const ContactButton = ({ label, link }) => {
        return (
            <a
                href={link}
                onClick={(e) => {
                    window.location = link;
                    e.preventDefault();
                }}
                className={bem('contactButton')}
                dir="ltr"
            >
                {label}
            </a>
        );
    };

    const content = !canAddToBag && useContactUs({ selectedProduct: reservation, isSizeAndFit: false, isReservation: true }) || {};
    const { call = '', emailLabel, sendEmailStructure = {} } = content;
    const { email = '', subject = '', body = '' } = sendEmailStructure;

    return (
        <LineItem {...reservation} messages={messages} onClick={onClick} subClassName={{ inline: true, unavailable: !canAddToBag }} productUrl={reservation?.details?.productUrl}>
            <div className={bem(null)}>
                <Notification
                    className={bem('date')}
                    type="info"
                    message={
                        canAddToBag ?
                            messages?.reservations?.date?.({expiryDate: reservation.reservationExpiryDate}) ?? '' :
                            messages?.reservations?.unavailableLocation?.() ?? ''
                    }
                >
                    {!canAddToBag && content &&
                        <div className={bem('contactButtons')}>
                            <ContactButton label={emailLabel} link={`mailto:${email}?&subject=${subject}&body=${body}`} />
                            {call &&
                                <>
                                    <span className={bem('divider')} />
                                    <ContactButton label={call} link={`tel:${call}`} />
                                </>
                            }
                        </div>
                    }
                </Notification>
                {canAddToBag && (!itemAdded ? (
                    <Tooltip
                        type="error"
                        message={errorMessage}
                        defaultOpen={addToCartRequested && itemFailed === reservation.partNumber}
                        key={itemFailed}
                        childWrapperClassName={bem('buy')}
                    >
                        <Button
                            onClick={() => {
                                setAddToCartRequested(true)
                                addToCart(reservation.partNumber, { addToCartLoading: false, reservationId, includeShoppingCart: true }).then((response) => {
                                    setAddToCartRequested(false)
                                    if (response?.type === 'ADD_TO_CART_SUCCESS') {
                                        reservationId && setReservationsInCart([...reservationsInCart, reservationId])
                                    }
                                })
                            }}
                            type="secondary"
                        >
                            {messages.reservations?.buy?.()}
                        </Button>
                    </Tooltip>
                ) : (
                    <div className={bem('addedToBag')}>
                        <div className={bem('addedToBagMessage')}>
                            {messages.reservations?.addedToBag?.()}
                        </div>
                    </div>
                ))}
            </div>
        </LineItem>
    )
}
